import { THEME_COLOR_TOKENS, THEME_FONT_KEYS, THEME_RADIUS_KEYS, getByPath, isRecord, setByPath } from './tokens';
import { toCssColor, toRadiusRem, toRgbChannels } from './color';
import type { IResolvedTheme, IThemeColors, IThemeDefinition, IThemeOverride, ThemeFontFamilyType } from '../types';

/** 逐 token 解析；onInvalid 决定非法值是回退（运行时）还是抛错（配置期） */
function resolveWith(input: unknown, base: IResolvedTheme, onInvalid: (field: string) => void): IResolvedTheme {
  const source = isRecord(input) ? input : {};
  const colors: Record<string, unknown> = {};
  for (const { path, format } of THEME_COLOR_TOKENS) {
    const raw = getByPath(source.colors, path);
    const value = raw === undefined ? null : format === 'channels' ? toRgbChannels(raw) : toCssColor(raw);
    if (raw !== undefined && value === null) onInvalid(`colors.${path.join('.')}`);
    setByPath(colors, path, value ?? getByPath(base.colors, path));
  }
  const radiusSource = isRecord(source.radius) ? source.radius : {};
  const radius = {} as IResolvedTheme['radius'];
  for (const key of THEME_RADIUS_KEYS) {
    const raw = radiusSource[key];
    const value = raw === undefined ? null : toRadiusRem(raw);
    if (raw !== undefined && value === null) onInvalid(`radius.${key}`);
    radius[key] = value ?? base.radius[key];
  }
  const isFontValid = THEME_FONT_KEYS.includes(source.fontFamily as ThemeFontFamilyType);
  if (source.fontFamily !== undefined && !isFontValid) onInvalid('fontFamily');
  return {
    colors: colors as unknown as IThemeColors,
    radius,
    fontFamily: isFontValid ? (source.fontFamily as ThemeFontFamilyType) : base.fontFamily
  };
}

/**
 * 运行时归一化入口（后台素材、缓存、iframe payload）：
 * 逐 token 严格校验，缺失或非法回退 base，未知字段丢弃，永不抛错。
 */
export function resolveTheme(input: unknown, base: IResolvedTheme): IResolvedTheme {
  return resolveWith(input, base, () => {});
}

/** 主题定义 → 运行时快照；任一字段非法即抛错（配置期尽早暴露问题） */
export function toResolvedTheme(definition: IThemeDefinition): IResolvedTheme {
  const invalid: string[] = [];
  const empty = { colors: {}, radius: {}, fontFamily: undefined } as unknown as IResolvedTheme;
  const theme = resolveWith(definition, empty, (field) => invalid.push(field));
  const missing = THEME_COLOR_TOKENS.filter(({ path }) => getByPath(theme.colors, path) === undefined)
    .map(({ path }) => `colors.${path.join('.')}`)
    .concat(THEME_RADIUS_KEYS.filter((key) => theme.radius[key] === undefined).map((key) => `radius.${key}`))
    .concat(theme.fontFamily ? [] : ['fontFamily']);
  if (invalid.length || missing.length) {
    throw new TypeError(`[theme] 主题定义不完整或非法：${[...invalid, ...missing].join(', ')}`);
  }
  return theme;
}

/** 声明主题定义并立即校验；返回原定义，保证静态色（games/pet）原样输出 */
export function defineTheme(definition: IThemeDefinition): IThemeDefinition {
  toResolvedTheme(definition);
  return definition;
}

/** 在 base 之上覆盖（颜色组 / 圆角按字段深合并） */
export function extendTheme(base: IThemeDefinition, override: IThemeOverride): IThemeDefinition {
  const colors = { ...base.colors } as Record<string, unknown>;
  for (const [key, value] of Object.entries(override.colors ?? {})) {
    if (value === undefined) continue;
    const current = colors[key];
    colors[key] = isRecord(value) && isRecord(current) ? { ...current, ...value } : value;
  }
  return defineTheme({
    colors: colors as unknown as IThemeColors,
    radius: { ...base.radius, ...override.radius },
    fontFamily: override.fontFamily ?? base.fontFamily
  });
}
